export type StoryboardMotif =
  | 'title'
  | 'curve'
  | 'donut'
  | 'cliff'
  | 'calculator'
  | 'levers'
  | 'stack'
  | 'category'
  | 'threeUp'
  | 'pillars'
  | 'contract'
  | 'loop'
  | 'snak'
  | 'moat'
  | 'stages'
  | 'sliders'
  | 'bars'
  | 'close'

export interface StoryboardFrame {
  /** Slide position in the deck */
  number: number
  /** Short section label */
  eyebrow: string
  /** Slide headline */
  title: string
  /** One-line narrative purpose of this frame */
  takeaway: string
  /** Two or three supporting proof points */
  points: string[]
  motif: StoryboardMotif
}

export const STORYBOARD_ACT_LABELS: Record<string, string> = {
  problem: 'Act I · The Problem',
  solution: 'Act II · The Solution',
  business: 'Act III · The Business',
}

export const storyboardFrames: StoryboardFrame[] = [
  {
    number: 1,
    eyebrow: 'Opening',
    title: 'Snakker — Legacy Brand Optimization',
    takeaway:
      'Set the frame: an AI-native engine that unlocks value from pharma’s revenue tail.',
    points: [
      'Category: Campaign Service Management',
      'Audience: investors & pharma leadership',
    ],
    motif: 'title',
  },
  {
    number: 2,
    eyebrow: 'Act I · The Problem',
    title: 'The Pharma Lifecycle',
    takeaway:
      'Every product runs R&D → Launch → Mature → Legacy. Snakker plays only in Legacy.',
    points: [
      'Legacy = patents expire, marketing pulled',
      'Strategic move: soften the cliff, extend the tail',
    ],
    motif: 'curve',
  },
  {
    number: 3,
    eyebrow: 'Act I · The Problem',
    title: 'Market Size',
    takeaway: 'The under-optimized part of pharma is enormous — and growing.',
    points: [
      '30–35% of big pharma revenue is mature/legacy',
      '$180–300B+ facing loss of exclusivity',
    ],
    motif: 'donut',
  },
  {
    number: 4,
    eyebrow: 'Act I · The Problem',
    title: 'Why Legacy Brands Die Early',
    takeaway:
      'The brands still have value — traditional marketing is simply too expensive to justify.',
    points: [
      'Small molecules can drop 90%+ at LOE',
      'Products withdrawn when revenue meets cost',
    ],
    motif: 'cliff',
  },
  {
    number: 5,
    eyebrow: 'Act I · The Problem',
    title: 'The Unlocked Value',
    takeaway: 'Quantify the prize: even 1% better is tens of millions a year.',
    points: [
      'Modeled on a €5–60B division at 35% legacy',
      'Interactive: adjust portfolio, see value',
    ],
    motif: 'calculator',
  },
  {
    number: 6,
    eyebrow: 'Act II · The Solution',
    title: 'Why AI Alone Isn’t Enough',
    takeaway:
      'AI collapses content cost, but execution is the real bottleneck.',
    points: [
      'Strategy + compliance + distribution + measurement',
      'Generalist AI fails inside regulated pharma',
    ],
    motif: 'levers',
  },
  {
    number: 7,
    eyebrow: 'Act II · The Solution',
    title: 'Platform Architecture',
    takeaway: 'A closed loop: Think · Create · Learn · Improve.',
    points: [
      'AI Intelligence Engine · Data Supply Chain · App Suite',
      'Snak Composer, Player, Distribution, Analytics',
    ],
    motif: 'stack',
  },
  {
    number: 8,
    eyebrow: 'Act II · The Solution',
    title: 'A New Category',
    takeaway:
      'Not an agency, not a SaaS tool — managed execution with software economics.',
    points: [
      'Campaign Service Management',
      'One operating partner per account',
    ],
    motif: 'category',
  },
  {
    number: 9,
    eyebrow: 'Act II · The Solution',
    title: 'Three Levers',
    takeaway:
      'Reduce erosion · increase profitability · prolong lifespan — they compound.',
    points: [
      'Continuous engagement keeps HCPs connected',
      'Positive revenue-to-cost gap for longer',
    ],
    motif: 'threeUp',
  },
  {
    number: 10,
    eyebrow: 'Act II · The Solution',
    title: 'The Operating Model',
    takeaway: 'Four pillars that make legacy campaigns economically viable.',
    points: [
      'Existing assets · rapid deployment',
      'Behavioral capture · continuous optimization',
    ],
    motif: 'pillars',
  },
  {
    number: 11,
    eyebrow: 'Act III · The Business',
    title: 'The Business Model',
    takeaway:
      'We run the annual campaign calendar for legacy portfolios — one contract per account.',
    points: [
      '4–6 campaigns per product per year',
      'GSK · Solventum · Leo Pharma · Roche live today',
      'Potential: 100–135 countries · 20–60 brands',
    ],
    motif: 'contract',
  },
  {
    number: 12,
    eyebrow: 'Act III · The Business',
    title: 'What Snakker Does',
    takeaway:
      'Ingest approved content → transform → publish → learn → improve.',
    points: [
      'Operating layer between strategy and execution',
      'Every interaction sharpens the next campaign',
    ],
    motif: 'loop',
  },
  {
    number: 13,
    eyebrow: 'Act III · The Business',
    title: 'Campaign Example',
    takeaway: 'Make it tangible: a real Snak in the HCP journey.',
    points: [
      'Short, interactive, TA-relevant',
      'Awareness → Education → Engagement → Loyalty',
    ],
    motif: 'snak',
  },
  {
    number: 14,
    eyebrow: 'Act III · The Business',
    title: 'The Moat',
    takeaway: 'Four compounding assets form an unbreakable flywheel.',
    points: [
      'Medical storytelling · engagement platform',
      'Pharma distribution · behavioural dataset',
    ],
    motif: 'moat',
  },
  {
    number: 15,
    eyebrow: 'Act III · The Business',
    title: 'Short to Mid Term Strategy',
    takeaway:
      'Shift sales focus from Stage I pilots to Stage II–IV enterprise deals.',
    points: [
      'Today 85% Stage I → target 10/20/30/40 across I–IV',
      'Stage II: Solventum, Menarini, Roche, AbbVie, CSL Vifor',
      'Stage III: GSK, Leo Pharma',
    ],
    motif: 'stages',
  },
  {
    number: 16,
    eyebrow: 'Act III · The Business',
    title: 'Fast Marketing™ Revenue Calculator',
    takeaway:
      'See for yourself — move the sliders to see the revenue potential per account.',
    points: [
      'Master Snak €3,000 · localization €1,000 · insights €99/mo',
      'Benchmark: 20–60 brands × 100–135 countries × 4–6 campaigns',
    ],
    motif: 'sliders',
  },
  {
    number: 17,
    eyebrow: 'Act III · The Business',
    title: 'Financial Projections',
    takeaway: 'DKK 3.9M in 2026 to DKK 36M in 2029, breakeven in 2027.',
    points: [
      'Revenue: 3.9 → 12.82 → 22.75 → 36 (DKK M)',
      'Net result: −2.95 → +0.35 → +5.45 → +18.6',
    ],
    motif: 'bars',
  },
  {
    number: 18,
    eyebrow: 'Close',
    title: 'The Ask',
    takeaway:
      'The legacy long tail is worth billions — Snakker is how pharma captures it.',
    points: [
      'Cleared with 15 of the top 30 pharma companies',
      'Morten Hjelmsø · mh@snakker.io',
    ],
    motif: 'close',
  },
]
